import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Check, X, MapPin, Clock, Mail, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { beginSocialPulseLogin, ssoConfigured } from "@/lib/socialpulseOidc";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const KIND_COLORS = { story: "bg-pink-50 text-pink-700", event: "bg-blue-50 text-blue-700", correction: "bg-amber-50 text-amber-700", claim: "bg-purple-50 text-purple-700", venue: "bg-emerald-50 text-emerald-700" };

export default function Moderation() {
  const { user, ready } = useAuth();
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    if (status === "digest") {
      api.get("/moderation/digest-preview")
        .then((r) => setDigest(r.data))
        .catch(() => setDigest(null))
        .finally(() => setLoading(false));
      return;
    }
    api.get("/moderation/contributions", { params: { status } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  const act = async (id, action) => {
    try {
      await api.post(`/moderation/contributions/${id}/${action}`);
      toast.success(action === "approve" ? "Approved — stories go live immediately." : "Rejected.");
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch {
      toast.error("Action failed. Please retry.");
    }
  };

  if (!ready) return null;

  if (!user) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4" data-testid="moderation-signin-required">
        <ShieldCheck className="w-10 h-10 text-purple-400" />
        <p className="font-bold text-slate-700">Sign in required to access moderation.</p>
        <button
          data-testid="moderation-signin-btn"
          onClick={() => (ssoConfigured() ? beginSocialPulseLogin("/moderation") : toast.info("SocialPulse SSO is being configured — check back shortly."))}
          className="gradient-btn text-white font-bold px-6 py-2.5 rounded-full text-sm"
        >
          Sign in with SocialPulse
        </button>
      </div>
    );
  }

  if (user.role !== "admin") {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-3" data-testid="moderation-forbidden">
        <ShieldCheck className="w-10 h-10 text-rose-400" />
        <p className="font-bold text-slate-700">Admin role required.</p>
        <p className="text-sm text-slate-500">Signed in as {user.email}</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="moderation-page">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500 mb-3">Moderation Dashboard</p>
      <h1 className="font-display text-4xl font-black text-slate-900">Community <span className="gradient-text">Review Queue.</span></h1>
      <p className="mt-2 text-slate-600 text-sm">Approve or reject pending stories, event submissions and corrections. Approved stories publish instantly.</p>

      <Tabs value={status} onValueChange={setStatus} className="mt-8">
        <TabsList className="rounded-full">
          <TabsTrigger value="pending" data-testid="moderation-tab-pending" className="rounded-full">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="moderation-tab-approved" className="rounded-full">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="moderation-tab-rejected" className="rounded-full">Rejected</TabsTrigger>
          <TabsTrigger value="digest" data-testid="moderation-tab-digest" className="rounded-full">Digest Preview</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="py-16 flex justify-center"><span className="eq-bars" style={{ height: 30 }}><span /><span /><span /><span /><span /></span></div>
      ) : status === "digest" ? (
        !digest ? (
          <p className="py-16 text-center text-slate-500" data-testid="digest-error">Could not load the digest preview.</p>
        ) : (
          <div className="mt-6" data-testid="digest-preview">
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 font-black" data-testid="digest-next-send">
                <CalendarClock className="w-3.5 h-3.5" /> Sends {new Date(digest.next_send).toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </span>
              <span>Weekly cron · Mondays 9:00 UTC · sent to the site owner</span>
            </div>
            <div className="mt-4 bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden max-w-2xl">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center gap-2 text-xs text-slate-500">
                <Mail className="w-4 h-4 text-pink-600" />
                <span className="font-bold text-slate-700">[MusicPulse] Weekly digest: {digest.total} new submission(s), {digest.pending} pending</span>
              </div>
              <div className="p-6">
                <h2 className="font-bold text-lg text-slate-900">Your weekly MusicPulse digest</h2>
                <p className="text-sm text-slate-500 mt-1">{digest.total} new submission(s) in the last 7 days · {digest.pending} awaiting review.</p>
                {digest.groups.length === 0 && <p className="text-sm text-slate-500 mt-4" data-testid="digest-empty">No new submissions this week.</p>}
                {digest.groups.map((g) => (
                  <div key={g.kind} className="mt-4" data-testid={`digest-group-${g.kind}`}>
                    <p className="text-sm font-black text-slate-900">{g.kind.charAt(0).toUpperCase() + g.kind.slice(1)} submissions ({g.count})</p>
                    {g.items.map((it, i) => (
                      <p key={i} className="text-sm text-slate-600 mt-0.5">• {it.title?.slice(0, 80)} — {it.name}</p>
                    ))}
                    {g.more > 0 && <p className="text-xs text-slate-400 mt-0.5">+ {g.more} more</p>}
                  </div>
                ))}
                <span className="mt-6 inline-block px-6 py-2.5 rounded-full bg-pink-600 text-white text-sm font-bold">Open review queue</span>
                <p className="text-[11px] text-slate-400 mt-4">Sent by MusicPulse. We never ask for your password by email.</p>
              </div>
            </div>
          </div>
        )
      ) : items.length === 0 ? (
        <p className="py-16 text-center text-slate-500" data-testid="moderation-empty">No {status} contributions.</p>
      ) : (
        <div className="space-y-4 mt-6">
          {items.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={`moderation-item-${c.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide ${KIND_COLORS[c.kind] || "bg-slate-100 text-slate-600"}`}>{c.kind}</span>
                <h2 className="font-bold text-slate-900">{c.title}</h2>
                <span className="ml-auto text-[11px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(c.submitted_at).toLocaleString()}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-2 flex-wrap">
                <span className="font-bold text-slate-700">{c.name}</span>
                {c.location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3 text-pink-500" /> {c.location}</span>}
                {c.email && <span>· {c.email}</span>}
              </p>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{c.body}</p>
              {c.photo_file_id && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${c.photo_file_id}`} alt="Submitted venue" className="mt-3 w-40 h-28 rounded-xl object-cover border border-slate-200" data-testid={`moderation-photo-${c.id}`} />
              )}
              {c.media_link && <a href={c.media_link} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-pink-600 mt-2 inline-block">View attached media ↗</a>}
              {status === "pending" && (
                <div className="flex gap-3 mt-4">
                  <button data-testid={`moderation-approve-${c.id}`} onClick={() => act(c.id, "approve")} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-emerald-600 text-white text-xs font-black hover:bg-emerald-700 transition-colors">
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button data-testid={`moderation-reject-${c.id}`} onClick={() => act(c.id, "reject")} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-rose-50 text-rose-700 text-xs font-black hover:bg-rose-100 transition-colors">
                    <X className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              )}
              {status !== "pending" && c.reviewed_by && <p className="text-[11px] text-slate-400 mt-3">Reviewed by {c.reviewed_by}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
